import { useState, useCallback, useMemo } from "react";
import type { ColumnDefinition, SortConfig } from "@/hooks/useSalesReportColumns";

interface ColumnState {
  visibleGroupColumns: string[];
  groupColumnOrder: string[];
  visiblePaymentColumns: string[];
  paymentColumnOrder: string[];
  groupSortConfig: SortConfig | null;
  paymentSortConfig: SortConfig | null;
}

// Colunas do cabeçalho do grupo (cliente)
const DEFAULT_GROUP_COLUMNS: ColumnDefinition[] = [
  { id: "clientName", labelKey: "Cliente", accessor: "clientName", sortable: true, align: "left", defaultVisible: true },
  { id: "quantidadePagamentos", labelKey: "Pagamentos", accessor: "quantidadePagamentos", sortable: true, align: "center", defaultVisible: true },
  { id: "totalAberto", labelKey: "Total Aberto", accessor: "totalAberto", sortable: true, align: "right", defaultVisible: true },
  { id: "totalAtrasado", labelKey: "Em Atraso", accessor: "totalAtrasado", sortable: true, align: "right", defaultVisible: true },
  { id: "maiorAtraso", labelKey: "Maior Atraso", accessor: "maiorAtraso", sortable: true, align: "right", defaultVisible: true },
];

// Colunas dos pagamentos expandidos
const DEFAULT_PAYMENT_COLUMNS: ColumnDefinition[] = [
  { id: "description", labelKey: "Descrição", accessor: "description", sortable: true, align: "left", defaultVisible: true },
  { id: "valor_aberto", labelKey: "Valor", accessor: "valor_aberto", sortable: true, align: "right", defaultVisible: true },
  { id: "due_date", labelKey: "Vencimento", accessor: "due_date", sortable: true, align: "center", defaultVisible: true },
  { id: "dias_atraso", labelKey: "Atraso", accessor: "dias_atraso", sortable: true, align: "center", defaultVisible: true },
  { id: "faixa", labelKey: "Faixa", accessor: "faixa", sortable: false, align: "center", defaultVisible: true },
  { id: "status", labelKey: "Status", accessor: "status", sortable: true, align: "center", defaultVisible: true },
  { id: "vendedor", labelKey: "Vendedor", accessor: "vendedor", sortable: true, align: "left", defaultVisible: true },
];

const STORAGE_KEY = "receivables-grouped-columns";

const getDefaultState = (): ColumnState => ({
  visibleGroupColumns: DEFAULT_GROUP_COLUMNS.filter(c => c.defaultVisible).map(c => c.id),
  groupColumnOrder: DEFAULT_GROUP_COLUMNS.map(c => c.id),
  visiblePaymentColumns: DEFAULT_PAYMENT_COLUMNS.filter(c => c.defaultVisible).map(c => c.id),
  paymentColumnOrder: DEFAULT_PAYMENT_COLUMNS.map(c => c.id),
  groupSortConfig: { column: "totalAtrasado", direction: "desc" },
  paymentSortConfig: null,
});

const loadFromStorage = (): ColumnState => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {
    // Ignore parsing errors
  }
  return getDefaultState();
};

const saveToStorage = (state: ColumnState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
};

export function useReceivablesGroupedColumns() {
  const [state, setState] = useState<ColumnState>(loadFromStorage);

  // Toggle visibility for group columns
  const toggleGroupColumnVisibility = useCallback((columnId: string) => {
    setState(prev => {
      const isVisible = prev.visibleGroupColumns.includes(columnId);
      const visibleGroupColumns = isVisible
        ? prev.visibleGroupColumns.filter(id => id !== columnId)
        : [...prev.visibleGroupColumns, columnId];
      
      const updated = { ...prev, visibleGroupColumns };
      saveToStorage(updated);
      return updated;
    });
  }, []);

  // Reorder group columns
  const reorderGroupColumns = useCallback((newOrder: string[]) => {
    setState(prev => {
      const updated = { ...prev, groupColumnOrder: newOrder };
      saveToStorage(updated);
      return updated;
    });
  }, []);

  // Toggle sort for group columns
  const toggleGroupSort = useCallback((columnId: string) => {
    setState(prev => {
      let groupSortConfig: SortConfig | null;
      
      if (prev.groupSortConfig?.column !== columnId) {
        groupSortConfig = { column: columnId, direction: "asc" };
      } else if (prev.groupSortConfig.direction === "asc") {
        groupSortConfig = { column: columnId, direction: "desc" };
      } else {
        groupSortConfig = null;
      }
      
      const updated = { ...prev, groupSortConfig };
      saveToStorage(updated);
      return updated;
    });
  }, []);

  // Toggle visibility for payment columns
  const togglePaymentColumnVisibility = useCallback((columnId: string) => {
    setState(prev => {
      const isVisible = prev.visiblePaymentColumns.includes(columnId);
      const visiblePaymentColumns = isVisible
        ? prev.visiblePaymentColumns.filter(id => id !== columnId)
        : [...prev.visiblePaymentColumns, columnId];
      
      const updated = { ...prev, visiblePaymentColumns };
      saveToStorage(updated);
      return updated;
    });
  }, []);

  // Reorder payment columns
  const reorderPaymentColumns = useCallback((newOrder: string[]) => {
    setState(prev => {
      const updated = { ...prev, paymentColumnOrder: newOrder };
      saveToStorage(updated);
      return updated;
    });
  }, []);

  // Toggle sort for payment columns
  const togglePaymentSort = useCallback((columnId: string) => {
    setState(prev => {
      let paymentSortConfig: SortConfig | null;
      
      if (prev.paymentSortConfig?.column !== columnId) {
        paymentSortConfig = { column: columnId, direction: "asc" };
      } else if (prev.paymentSortConfig.direction === "asc") {
        paymentSortConfig = { column: columnId, direction: "desc" };
      } else {
        paymentSortConfig = null;
      }
      
      const updated = { ...prev, paymentSortConfig };
      saveToStorage(updated);
      return updated;
    });
  }, []);

  // Reset to default
  const resetToDefault = useCallback(() => {
    const defaultState = getDefaultState();
    setState(defaultState);
    saveToStorage(defaultState);
  }, []);

  // Ordered visible group columns
  const orderedVisibleGroupColumns = useMemo(() => {
    return state.groupColumnOrder
      .filter(id => state.visibleGroupColumns.includes(id))
      .map(id => DEFAULT_GROUP_COLUMNS.find(col => col.id === id)!)
      .filter(Boolean);
  }, [state.groupColumnOrder, state.visibleGroupColumns]);

  // Ordered visible payment columns
  const orderedVisiblePaymentColumns = useMemo(() => {
    return state.paymentColumnOrder
      .filter(id => state.visiblePaymentColumns.includes(id))
      .map(id => DEFAULT_PAYMENT_COLUMNS.find(col => col.id === id)!)
      .filter(Boolean);
  }, [state.paymentColumnOrder, state.visiblePaymentColumns]);

  return {
    // Group columns
    groupColumns: DEFAULT_GROUP_COLUMNS,
    visibleGroupColumns: state.visibleGroupColumns,
    groupColumnOrder: state.groupColumnOrder,
    groupSortConfig: state.groupSortConfig,
    orderedVisibleGroupColumns,
    toggleGroupColumnVisibility,
    reorderGroupColumns,
    toggleGroupSort,
    
    // Payment columns
    paymentColumns: DEFAULT_PAYMENT_COLUMNS,
    visiblePaymentColumns: state.visiblePaymentColumns,
    paymentColumnOrder: state.paymentColumnOrder,
    paymentSortConfig: state.paymentSortConfig,
    orderedVisiblePaymentColumns,
    togglePaymentColumnVisibility,
    reorderPaymentColumns,
    togglePaymentSort,
    
    // Reset
    resetToDefault,
  };
}
